import { compose } from 'redux';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { push } from 'connected-react-router';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { get } from 'lodash';
import FormSubHeading from 'components/atoms/form/FormSubHeading';
import LoadingImage from 'components/atoms/LoadingImage';
import FieldValue from 'components/atoms/text/FieldValue';
import ButtonStandardCompact from 'components/atoms/buttons/compact/Primary';
import FormSectionHeading from 'components/atoms/form/FormSectionHeading';
import FormViewGrid from 'components/atoms/form/FormViewGrid';
import ButtonIconSecondary from 'components/molecules/buttons/ButtonIconSecondary';
import EntityUsersTable from 'components/molecules/tables/EntityUsersTable';
import EntityChildrenTable from 'components/molecules/tables/EntityChildrenTable';
import EditEntityBrandingContainer from 'containers/forms/EditEntityBrandingContainer';
import WorkOrderView from 'components/organisms/entity/view/WorkOrderView';
import CustomChannelView from 'components/organisms/entity/view/CustomChannelView';
import AnnotationsTableContainer from 'containers/tables/AnnotationsTableContainer';
import AuditViewContainer from 'containers/AuditViewContainer';
import {
    fetchEntity,
    fetchList as fetchEntitiesList,
    deleteEntity,
    fetchEntityChildren,
    fetchHierarchyDump,
    searchForOpportunities
} from 'services/entities/duck';
import {
    Tab,
    Tabs,
    TabList,
    TabPanel
} from 'components/organisms/Tabs';
import ProtectedComponent from 'hoc/ProtectedComponent';
import loadingGif from 'assets/loading.gif';
import { getObjectiveDisplayName } from 'helpers/objectiveSettings';
import { ENTITY_PLATFORMS } from 'constants/entityPlatforms';
import { ANNOTATION_READ, ENTITY_DELETE, SPEND_MARKUP_VIEW, MANAGE_ENTITY_OBJECTIVE, ENTITY_HIERARCHY_DUMP, AUDIT_READ } from 'constants/capabilities';
import ViewEntityAdvancedSetting from 'components/molecules/ViewEntityAdvancedSetting';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';

const TrackedCloneLink = TrackedComponentEnhancer({
    trackedName: 'Clone-Entity-From-Detailed-View',
    targetComponent: Link,
    sendToMixPanel: true
});

const EntityHeader = styled.div`
    color: ${props => props.theme.colors.black};
    font-size: 24px;
    letter-spacing: 1px;
    line-height: 32px;
    text-transform: uppercase;
    a {
        padding-left:15px;
        font-size: 20px;
    }

`;

const DeleteIconButton = ProtectedComponent({ requiredCapabilities: [ENTITY_DELETE] })(ButtonIconSecondary);
const AnnotationTablePC = ProtectedComponent({ requiredCapabilities: [ANNOTATION_READ] })(AnnotationsTableContainer);

const TABINDEX_INFO = 0;
const TABINDEX_USERS = 1;
const TABINDEX_CHILDREN = 2;
const TABINDEX_BRANDING = 3;
const TABINDEX_ANNOTATIONS = 4;
const TABINDEX_REVISIONS = 5;

const SpendMarkupProtectedComponent = ProtectedComponent({
    requiredCapabilities: [SPEND_MARKUP_VIEW]
})(props => (<React.Fragment>{props.children}</React.Fragment>));

const EntityObjectiveProtectedComponent = ProtectedComponent({
    requiredCapabilities: [MANAGE_ENTITY_OBJECTIVE]
})(props => (<React.Fragment>{props.children}</React.Fragment>));

const HierarchyDumpProtectedComponent = ProtectedComponent({
    requiredCapabilities: [ENTITY_HIERARCHY_DUMP]
})(props => (<React.Fragment>{props.children}</React.Fragment>));

// TODO: This container contains IMO (Julian) too much view styling or logic.
// The container should be responsible for connecting to the state and getting the data on mount
class EntityViewContainer extends Component {
    constructor() {
        super();
        this.state = {
            entityId: null,
            tabIndex: TABINDEX_INFO
        };
        this.onTabSelect = this.onTabSelect.bind(this);
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.match.params.id, 10);
        if (entityId !== state.entityId) {
            return {
                ...state,
                entityId,
                tabIndex: TABINDEX_INFO
            };
        }

        return null;
    }

    makeRequestsForData() {
        const entityId = this.state.entityId;
        if (entityId) {
            this.props.fetchEntity({ entityId });
            this.props.fetchEntitiesList([{ entityId }]);
        }
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.state.entityId;

        if (isEntityChanged) {
            this.makeRequestsForData();
        }
    }

    entityDashboardLink(entity) {
        return (
            <Link to={`/dashboard/${entity.id}/summary`} target="_blank" title="Go to dashboard">
                <span className="icon-open" />
            </Link>
        );
    }

    editEntityLink(entity) {
        return (
            <Link to={`/admin/entities/${entity.id}/edit`} title="Edit Entity">
                <span className="icon-edit" />
            </Link>
        );
    }

    cloneEntityLink(entity) {
        return (
            <TrackedCloneLink
                to={`/admin/entities/create?templateId=${entity.id}`} title="Copy entity"
                target="_blank">
                <span className="icon-clone" />
            </TrackedCloneLink>
        );
    }

    isSalesforce() {
        return get(this.props.entity, 'entityPlatform', ENTITY_PLATFORMS.default) === ENTITY_PLATFORMS.salesforce;
    }

    showWorkOrders() {
        const { entity = {} } = this.props;
        const workOrders = get(entity, 'workOrders', []);

        return <React.Fragment>
            <FormSectionHeading>Associated Work Orders</FormSectionHeading>
            {workOrders.length ?
                workOrders.map((workOrder, index) =>
                    <WorkOrderView
                        key={index}
                        entity={entity}
                        workOrder={workOrder}
                        searchForOpportunities={this.props.searchForOpportunities}
                    />) :
                <FieldValue>No Work Orders</FieldValue>}
        </React.Fragment>;
    }

    showCustomChannels() {
        const { entity = {} } = this.props;
        const embeddedLinks = get(entity, 'meta.embeddedLinks', []);

        return <React.Fragment>
            <FormSectionHeading>Custom Channels</FormSectionHeading>
            {embeddedLinks.length ?
                embeddedLinks.map((embeddedLink, index) =>
                    <CustomChannelView
                        key={index}
                        entity={entity}
                        embeddedLink={embeddedLink}
                    />) :
                <FieldValue>No Custom Channels</FieldValue>}
        </React.Fragment>;
    }

    onTabSelect(tabIndex) {
        this.setState({ tabIndex });

        if (tabIndex === TABINDEX_CHILDREN && this.props.entity && !this.props.entity.children) {
            const entityId = this.state.entityId;
            this.props.fetchEntityChildren({ entityId });
        }
    }

    render() {
        const entity = this.props.entity;
        const entitiesList = this.props.entitiesList || [];
        const parentEntityIds = get(entity, 'parentEntityIds', []);
        const entityIsDemo = get(entity, 'isDemo') ? 'Yes' : 'No';
        const entityBrand = get(entity, 'Brand.name', null);
        const entityBrandId = entity && entity.Brand ? entity.brandId : null;
        const entityLayerName = get(entity, 'BrandAlias.name', null);
        const entityUsers = get(entity, 'Users', []);
        const entityChildren = get(entity, 'children', []);
        const entityContactEmail = get(entity, 'contactEmail', null);
        const entitySalesEmail = get(entity, 'salesEmail', null);
        const overrideBrandAdvancedOptions = get(entity, 'meta.overrideBrand.advancedOptions', false);

        let showTotalActivities;
        let showTargetingTactics;
        let showeCPAonSummaryDashboard;
        let enableObjectiveSwitching;
        let showSemCpconv;
        let showSocialCpconv;
        let showFacebookDemographicData;
        let showSocialExpandModules;
        let enforceSummaryOnlyDashboardView;
        let showConversionRevenue;
        let showTtdSitesSummary;
        if (entityBrand && !overrideBrandAdvancedOptions) {
            showTotalActivities = get(entity, 'Brand.meta.showTotalActivities', false);
            showTargetingTactics = get(entity, 'Brand.meta.showTargetingTactics', false);
            showeCPAonSummaryDashboard = get(entity, 'Brand.meta.showeCPAonSummaryDashboard', false);
            enableObjectiveSwitching = get(entity, 'Brand.meta.enableObjectiveSwitching', false);
            showSemCpconv = get(entity, 'Brand.meta.showSemCpconv', false);
            showSocialCpconv = get(entity, 'Brand.meta.showSocialCpconv', false);
            showFacebookDemographicData = get(entity, 'Brand.meta.showFacebookDemographicData', false);
            showSocialExpandModules = get(entity, 'Brand.meta.showSocialExpandModules', false);
            enforceSummaryOnlyDashboardView = get(entity, 'Brand.meta.enforceSummaryOnlyDashboardView', false);
            showConversionRevenue = get(entity, 'Brand.meta.showConversionRevenue', false);
            showTtdSitesSummary = get(entity, 'Brand.meta.showTtdSitesSummary', false);
        } else {
            showTotalActivities = get(entity, 'meta.showTotalActivities', false);
            showTargetingTactics = get(entity, 'meta.showTargetingTactics', false);
            showeCPAonSummaryDashboard = get(entity, 'meta.showeCPAonSummaryDashboard', false);
            enableObjectiveSwitching = get(entity, 'meta.enableObjectiveSwitching', false);
            showSemCpconv = get(entity, 'meta.showSemCpconv', false);
            showSocialCpconv = get(entity, 'meta.showSocialCpconv', false);
            showFacebookDemographicData = get(entity, 'meta.showFacebookDemographicData', false);
            showSocialExpandModules = get(entity, 'meta.showSocialExpandModules', false);
            enforceSummaryOnlyDashboardView = get(entity, 'meta.enforceSummaryOnlyDashboardView', false);
            showConversionRevenue = get(entity, 'meta.showConversionRevenue', false);
            showTtdSitesSummary = get(entity, 'meta.showTtdSitesSummary', false);
        }

        const parentEntities = [];

        if (parentEntityIds.length !== 0 && entitiesList.length !== 0) {
            parentEntityIds.forEach((parentId) => {
                parentEntities.push(entitiesList.find(e => e.id === parentId));
            });
        }

        const spendMarkup = `${get(entity, 'meta.spendMarkup.value', 0)}%`;
        const globalObjective = getObjectiveDisplayName(get(entity, 'meta.programmaticObjective.global', 'ecpa'));
        const ObjectiveLabelCode = getObjectiveDisplayName(get(entity, 'meta.programmaticObjective.labelCode', 'ecpa'));

        return (
            <FormViewGrid fluid>
                <EntityHeader>
                    {get(entity, 'name', null)}
                    {entity ? this.editEntityLink(entity) : null}
                    {entity ? this.cloneEntityLink(entity) : null}
                    {entity ? this.entityDashboardLink(entity) : null}
                </EntityHeader>
                <Tabs forceRenderTabPanel selectedIndex={this.state.tabIndex} onSelect={this.onTabSelect}>
                    <TabList>
                        <Tab tabIndex={TABINDEX_INFO.toString()}>Info</Tab>
                        <Tab tabIndex={TABINDEX_USERS.toString()}>Users</Tab>
                        <Tab tabIndex={TABINDEX_CHILDREN.toString()}>Children</Tab>
                        <Tab tabIndex={TABINDEX_BRANDING.toString()}>Branding</Tab>
                        {this.props.currentUser ?
                            (this.props.currentUser.scope.includes('Annotation-Read')
                                && <Tab tabIndex={TABINDEX_ANNOTATIONS.toString()}>Annotations</Tab>)
                            : null}
                        {this.props.currentUser ?
                            (this.props.currentUser.scope.includes('Audit-Read')
                                && <Tab tabIndex={TABINDEX_REVISIONS.toString()}>Revisions</Tab>)
                            : null}
                    </TabList>
                    <TabPanel>
                        <Row>
                            <Col md={6}>
                                {this.showWorkOrders()}
                                {this.showCustomChannels()}
                                <Row>
                                    <Col xs={12}>
                                        <FormSectionHeading>Sales Email</FormSectionHeading>
                                        <FieldValue>{entitySalesEmail || (<span style={{ color: 'red' }}>No Sales Email</span>)}</FieldValue>
                                    </Col>
                                    <Col xs={12}>
                                        <FormSectionHeading>Contact Email</FormSectionHeading>
                                        <FieldValue>{entityContactEmail || 'No Contact Email'}</FieldValue>
                                    </Col>
                                </Row>

                                <FormSectionHeading>Display Configuration</FormSectionHeading>

                                <Row>
                                    <Col xs={6}><FormSubHeading>Brand</FormSubHeading></Col>
                                    <Col xs={6}><FormSubHeading>Layer Type</FormSubHeading></Col>
                                </Row>

                                {/* NOTE: this is a temporary flag until we implement brand overrides at the entity level
                                For now, we only want to set channel config at the entity level
                                when there is no brand association */}
                                {!entityBrand ?
                                    <FieldValue>No Associated Brand</FieldValue>
                                    :
                                    <Row>
                                        <Col xs={6}><Link to={`/admin/brands/${entityBrandId}`}>{entityBrand}</Link></Col>
                                        <Col xs={6}><FieldValue>{entityLayerName}</FieldValue></Col>
                                    </Row>}

                                <EntityObjectiveProtectedComponent>
                                    <Row>
                                        <Col xs={6}>
                                            <FormSubHeading>Objective Settings</FormSubHeading>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={6}><FieldValue>{globalObjective}</FieldValue></Col>
                                    </Row>
                                    <Row>
                                        <Col xs={6}>
                                            <FormSubHeading>Objective Label</FormSubHeading>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={6}><FieldValue>{ObjectiveLabelCode}</FieldValue></Col>
                                    </Row>
                                </EntityObjectiveProtectedComponent>

                                <SpendMarkupProtectedComponent>
                                    <Row>
                                        <Col xs={6}>
                                            <FormSubHeading>Spend Markup</FormSubHeading>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={6}><FieldValue>{spendMarkup}</FieldValue></Col>
                                    </Row>
                                </SpendMarkupProtectedComponent>

                                <FormSectionHeading>Parent Entities</FormSectionHeading>

                                {parentEntities.length === 0 &&
                                    <FieldValue>No Parent Entities</FieldValue>}

                                {parentEntities.map((parent, index) => (
                                    <React.Fragment key={index}>
                                        <Link to={`/admin/entities/${parent.id}`}>{parent.name}</Link><br />
                                    </React.Fragment>
                                ))}

                                <FormSectionHeading>Advanced Options</FormSectionHeading>

                                <ViewEntityAdvancedSetting
                                    showFlag={showTargetingTactics}
                                    brandName={entityBrand}
                                    displayName='Show Breakdown by Targeting Tactic'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showConversionRevenue}
                                    brandName={entityBrand}
                                    displayName='Conversion Revenue'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showTotalActivities}
                                    brandName={entityBrand}
                                    displayName='Include non-eCPA Activities (Total Activities)'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showSemCpconv}
                                    brandName={entityBrand}
                                    displayName='SEM Cost per Conversion'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showSocialCpconv}
                                    brandName={entityBrand}
                                    displayName='Social Cost per Conversion'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showFacebookDemographicData}
                                    brandName={entityBrand}
                                    displayName='Facebook Demographic Data'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showSocialExpandModules}
                                    brandName={entityBrand}
                                    displayName='Social Expand Modules'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showeCPAonSummaryDashboard}
                                    brandName={entityBrand}
                                    displayName='eCPA on Summary Channel'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={enableObjectiveSwitching}
                                    brandName={entityBrand}
                                    displayName='Objective Switching'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={enforceSummaryOnlyDashboardView}
                                    brandName={entityBrand}
                                    displayName='Enable Summary Mode: Removes links to channel specific detail pages'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <ViewEntityAdvancedSetting
                                    showFlag={showTtdSitesSummary}
                                    brandName={entityBrand}
                                    displayName='Show TV Channel Distributions'
                                    overrideBrandFlag={overrideBrandAdvancedOptions}
                                />

                                <Row>
                                    <Col xs={6}>
                                        <FormSectionHeading>Demo Mode</FormSectionHeading>
                                        <FieldValue>{entityIsDemo}</FieldValue>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={6}>
                                        <ButtonStandardCompact
                                            margin="40px 0px 0px 0px"
                                            type="submit"
                                            onClick={() => this.props.push(`/admin/entities/${this.props.match.params.id}/edit`)}>
                                            Edit
                                        </ButtonStandardCompact>
                                    </Col>
                                    <Col xs={6}>
                                        <DeleteIconButton
                                            margin="40px 0px 0px 0px"
                                            icon="trash"
                                            iconColor="#F00"
                                            onClick={() => this.props.deleteEntity(entity)}>
                                            Delete
                                        </DeleteIconButton>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={6}>

                            </Col>
                        </Row>
                    </TabPanel>
                    <TabPanel>
                        <Row>
                            <Col md={6}>
                                {entityUsers.length === 0 ?
                                    <FieldValue>No Users to display</FieldValue>
                                    :
                                    <React.Fragment>
                                        <FormSectionHeading>Users With Access</FormSectionHeading>
                                        <EntityUsersTable
                                            data={entityUsers} />
                                    </React.Fragment>
                                }
                            </Col>
                            <Col md={6}>

                            </Col>
                        </Row>
                    </TabPanel>
                    <TabPanel>
                        <HierarchyDumpProtectedComponent>
                            <ButtonStandardCompact
                                margin="40px 0px 0px 0px"
                                type="submit"
                                onClick={() => this.props.fetchHierarchyDump(this.state.entityId)}>
                                Dump Hierarchy
                            </ButtonStandardCompact>
                        </HierarchyDumpProtectedComponent>
                        {entityChildren === null ?
                            <LoadingImage src={loadingGif} alt='loading...' />
                            :
                            <React.Fragment>
                                <FormSectionHeading>Entity Tree</FormSectionHeading>
                                <EntityChildrenTable data={entityChildren || []} />
                            </React.Fragment>
                        }
                    </TabPanel>
                    <TabPanel>
                        {this.props.entity ?
                            <EditEntityBrandingContainer />
                            :
                            <span>No Entity Loaded</span>
                        }
                    </TabPanel>
                    {this.props.currentUser ?
                        (this.props.currentUser.scope.includes(ANNOTATION_READ)
                            && <TabPanel>
                                {this.props.entity ?
                                    <AnnotationTablePC
                                        isEntitySpecific={true}
                                        entityId={this.state.entityId}
                                    />
                                    :
                                    <span>No Annotations</span>
                                }
                            </TabPanel>
                        ) : null}
                    {this.props.currentUser ?
                        (this.props.currentUser.scope.includes(AUDIT_READ)
                            && <TabPanel>
                                <AuditViewContainer
                                    entityId={this.state.entityId}
                                />
                            </TabPanel>
                        ) : null}
                </Tabs>
            </FormViewGrid>
        );
    }
}

const mapStateToProps = state => ({
    entity: state.entities.entity,
    entitiesList: state.entities.list,
    initialValues: state.entities.entity,
    currentUser: state.users.currentUser
});

export default compose(
    connect(mapStateToProps,
        {
            fetchEntity,
            searchForOpportunities,
            fetchEntitiesList,
            push,
            deleteEntity,
            fetchEntityChildren,
            fetchHierarchyDump
        }),
)(EntityViewContainer);



// WEBPACK FOOTER //
// ./src/containers/EntityViewContainer.js